import { useEffect, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { useCompanySettingsForm } from '@/hooks/useCompanySettingsForm';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { CompanyInfoSection } from '@/components/company/CompanyInfoSection';

export function CompanySettingsPage() {
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const { canManageTeam } = useCompanyRole();

  const { form, setField, resetForm, isSubmitting, setIsSubmitting } = useCompanySettingsForm();

  const updateCompany = useMutation(api.companies.mutations.update);

  useEffect(() => {
    if (currentCompany) {
      resetForm({
        name: currentCompany.name,
        email: currentCompany.email ?? '',
        phoneNumber: currentCompany.phoneNumber ?? '',
        address: currentCompany.address ?? '',
        website: currentCompany.website ?? '',
        country: currentCompany.country ?? '',
      });
    }
  }, [currentCompany, resetForm]);

  const handleSaveInfo = useCallback(async () => {
    if (!currentCompany) return;
    setIsSubmitting(true);
    try {
      await updateCompany({
        companyId: currentCompany._id,
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phoneNumber: form.phoneNumber.trim() || undefined,
        address: form.address.trim() || undefined,
        website: form.website.trim() || undefined,
        country: form.country || undefined,
      });
      toast.success('Company info updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update company info');
    } finally {
      setIsSubmitting(false);
    }
  }, [currentCompany, form, updateCompany, setIsSubmitting]);

  if (companyLoading) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-6">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!canManageTeam) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShieldAlert className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">Access Denied</h3>
          <p className="text-sm text-muted-foreground">
            Only company admins can access settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Company Settings</h1>

      <CompanyInfoSection
        form={form}
        isSubmitting={isSubmitting}
        onSetField={setField}
        onSave={handleSaveInfo}
      />
    </div>
  );
}
